/**
 * Jain Gaurav Yatra — Members (read-only list).
 * Backend: GET /jain_gaurav_yatra/members/  (cookie-session auth + X-Tenant-ID).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

/** Like jsonHeaders but without Content-Type, so the browser sets the multipart boundary. */
function multipartHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg = pick(d?.message) ?? pick(d?.detail);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const MEMBERS_PATH = "/jain_gaurav_yatra/members/";

/** Page-facing member shape. */
export interface Member {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  created_at: string;
  visited: number;
  total: number;
  isAdmin: boolean;
  /** Soft-delete flag. A devotee who deactivated via DELETE /user/me/ is is_active=false. */
  is_active: boolean;
  /** Profile photo URL served by the backend, when the devotee has uploaded one. */
  photo: string | null;
}

function numOr(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === "") {return null;}
  return String(v);
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function toMember(row: Record<string, unknown>): Member {
  const progress = obj(row.progress);
  return {
    id: String(row.id ?? ""),
    full_name: strOrNull(row.full_name),
    email: strOrNull(row.email),
    phone: strOrNull(row.phone),
    location: strOrNull(row.location),
    created_at: String(row.created_at ?? ""),
    visited: numOr(progress.visited, 0),
    total: numOr(progress.total, 0),
    isAdmin: row.is_staff === true || String(row.role ?? "").toLowerCase() === "admin",
    is_active: row.is_active !== false,
    photo: strOrNull(row.member_photo ?? row.photo ?? row.profile_photo ?? row.avatar),
  };
}

/** Fields accepted when editing a member. */
export interface MemberWrite {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  /** New profile photo chosen in the editor; when set, the request is sent as multipart. */
  photoFile?: File | null;
}

/** Which slice of the roster to fetch. Backend defaults to active-only when unfiltered. */
export type MemberFilter = "active" | "inactive" | "all";

function memberFilterQuery(filter: MemberFilter): string {
  // Deactivated devotees are only returned when is_active is passed explicitly.
  if (filter === "inactive") {return "?is_active=false";}
  if (filter === "all") {return "?is_active=all";}
  return "";
}

export async function listMembers(filter: MemberFilter = "active"): Promise<Member[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${MEMBERS_PATH}${memberFilterQuery(filter)}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toMember);
}

/** Fields accepted when creating a member. */
export interface MemberCreate {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  role: string;
  is_staff: boolean;
  /** Optional profile photo; when set, the request is sent as multipart. */
  photoFile?: File | null;
}

export async function createMember(payload: MemberCreate): Promise<Member> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  let body: BodyInit;
  let headers: HeadersInit;
  if (payload.photoFile) {
    const fd = new FormData();
    fd.append("full_name", payload.full_name);
    fd.append("email", payload.email);
    fd.append("phone", payload.phone);
    fd.append("location", payload.location);
    fd.append("role", payload.role);
    fd.append("is_staff", payload.is_staff ? "true" : "false");
    fd.append("member_photo", payload.photoFile);
    body = fd;
    headers = multipartHeaders();
  } else {
    body = JSON.stringify({
      full_name: payload.full_name,
      email: payload.email,
      phone: payload.phone,
      location: payload.location,
      role: payload.role,
      is_staff: payload.is_staff,
    });
    headers = jsonHeaders();
  }
  const res = await fetch(`${base}${MEMBERS_PATH}`, {
    method: "POST",
    headers,
    credentials: "include",
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toMember(data as Record<string, unknown>);
}

export async function updateMember(id: string, payload: MemberWrite): Promise<Member> {
  const base = apiBase();
  let body: BodyInit;
  let headers: HeadersInit;
  if (payload.photoFile) {
    const fd = new FormData();
    fd.append("full_name", payload.full_name);
    fd.append("email", payload.email);
    fd.append("phone", payload.phone);
    fd.append("location", payload.location);
    fd.append("member_photo", payload.photoFile);
    body = fd;
    headers = multipartHeaders();
  } else {
    body = JSON.stringify({
      full_name: payload.full_name,
      email: payload.email,
      phone: payload.phone,
      location: payload.location,
    });
    headers = jsonHeaders();
  }
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/`, {
    method: "PATCH",
    headers,
    credentials: "include",
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toMember(data as Record<string, unknown>);
}

export async function deleteMember(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await res.json().catch(() => ({}));
    throw new Error(errMessage(data, res.status));
  }
}

/**
 * Restore a soft-deleted devotee: POST /members/:id/restore/  (admin only, no body).
 * Re-enables OTP login. Throws "This member is already active." (code: already_active) on 400.
 */
export async function restoreMember(id: string): Promise<Member | null> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/restore/`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  // Success payload: { detail, code, member: {...} }
  const m = (data as { member?: unknown })?.member;
  return m ? toMember(m as Record<string, unknown>) : null;
}

/** Promote a member to admin: POST /members/:id/make-admin/ */
export async function makeMemberAdmin(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/make-admin/`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(errMessage(data, res.status));
  }
}
